import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { ArrowLeft, Pencil, PaperPlaneTilt, Copy, CopySimple, PlusCircle } from '@phosphor-icons/react';
import { format } from 'date-fns';
import api from '../../../utils/api';
import { filterLayoutBlocks } from '../../../utils/proposalCraftUtils';
import { getLineItemTotal, getUnitPriceWithTax } from '../../../utils/taxCalculations';
import { usePlatformAlerts } from '../../../context/PlatformAlertsContext';
import AgreementAcceptedAdditionalInfo from '../../Proposal/AgreementAcceptedAdditionalInfo';

const STATUS_COLORS = {
  draft: 'default',
  sent: 'info',
  accepted: 'success',
  declined: 'error',
  expired: 'default',
  scheduled: 'warning'
};

function ProposalDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showError } = usePlatformAlerts();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [creatingAddendum, setCreatingAddendum] = useState(false);
  const [threadAgreements, setThreadAgreements] = useState([]);

  const fetchProposal = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/agreements/${id}`);
      setProposal(res.data);
    } catch {
      const message = 'Failed to load agreement';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [id, showError]);

  useEffect(() => {
    fetchProposal();
  }, [fetchProposal]);

  useEffect(() => {
    const businessId = proposal?.business?._id || proposal?.business;
    const leadId = proposal?.lead?._id || proposal?.lead;
    if (!businessId || !leadId) return;

    const loadThreadAgreements = async () => {
      try {
        const { data } = await api.get(`/api/agreements/business/${businessId}`, { params: { leadId } });
        setThreadAgreements(data || []);
      } catch {
        setThreadAgreements([]);
      }
    };

    loadThreadAgreements();
  }, [proposal?.business, proposal?.lead]);

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await api.post(`/api/agreements/${id}/send`);
      setProposal((p) => (p ? { ...p, ...res.data } : res.data));
      if (res.data?.agreementUrl) {
        navigator.clipboard?.writeText(res.data.agreementUrl);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send agreement';
      setError(message);
      showError(message);
    } finally {
      setSending(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    setSavingTemplate(true);
    setError('');
    try {
      await api.post(`/api/agreements/${id}/save-as-template`);
      setError('');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save template';
      setError(message);
      showError(message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCreateAddendum = async () => {
    if (!proposal?._id) return;
    setCreatingAddendum(true);
    setError('');
    try {
      const { data } = await api.post(`/api/agreements/${proposal._id}/addendum`);
      if (data?._id) {
        navigate(`/supplier/agreements/${data._id}/edit`);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create addendum';
      setError(message);
      showError(message);
    } finally {
      setCreatingAddendum(false);
    }
  };

  const clientUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;

  const formatCurrency = (amount, currency = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);

  const isAccepted = proposal?.status === 'accepted';
  const isOriginal = !proposal?.parentAgreement && proposal?.agreementType !== 'addendum';
  const rootAgreementId = (proposal?.parentAgreement?._id || proposal?.parentAgreement || proposal?._id || '').toString();
  const groupedThread = useMemo(() => {
    if (!rootAgreementId) return [];
    return threadAgreements
      .filter((item) => {
        const itemId = String(item._id);
        const parentId = item.parentAgreement
          ? String(item.parentAgreement?._id || item.parentAgreement)
          : null;
        return itemId === rootAgreementId || parentId === rootAgreementId;
      })
      .sort((a, b) => (a.versionNumber || 1) - (b.versionNumber || 1));
  }, [rootAgreementId, threadAgreements]);
  const visibleLineItems = useMemo(() => {
    const items = proposal?.lineItems || [];
    if (!isAccepted) return items;
    const selectedIds = new Set(
      (proposal?.acceptance?.selectedOptionalIds || []).map(String)
    );
    return items.filter((item) => !item.isOptional || selectedIds.has(String(item._id)));
  }, [proposal?.lineItems, isAccepted, proposal?.acceptance?.selectedOptionalIds]);

  const total = visibleLineItems.reduce((sum, item) => sum + getLineItemTotal(item), 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !proposal) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/supplier/agreements')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  const blocks = proposal?.content?.blocks || [];
  const layoutBlocks = filterLayoutBlocks(blocks);
  const legacyFreeform = layoutBlocks.length === 0 && blocks.length === 0;
  const firstHeadingText = layoutBlocks.find((b) => b?.type === 'heading')?.props?.text?.trim();
  const proposalTitle = firstHeadingText || `Agreement for ${proposal?.lead?.name || '—'}`;

  const renderPricingContent = () => (
    <>
      <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
        {visibleLineItems.map((item) => {
          const qty = item.quantity || 1;
          const unitWithTax = getUnitPriceWithTax(item);
          const lineTotal = getLineItemTotal(item);
          return (
            <Box
              key={item._id}
              component="li"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                <Typography variant="body2" component="span">
                  {item.description}
                  {item.isOptional && !isAccepted && ' (optional)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                  {qty}× {formatCurrency(unitWithTax, proposal.currency)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {formatCurrency(lineTotal, proposal.currency)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Total: {formatCurrency(total, proposal?.currency)}</Typography>
      </Box>
    </>
  );

  const renderBlock = (block, keyPrefix = 'block') => {
    const blockKey = `${keyPrefix}-${block.id || 'item'}`;
    switch (block.type) {
      case 'heading': {
        const Heading = block.props?.level === 1 ? 'h4' : block.props?.level === 3 ? 'h6' : 'h5';
        return (
          <Typography key={blockKey} component={Heading} sx={{ mt: 2, mb: 1 }}>
            {block.props?.text || ''}
          </Typography>
        );
      }
      case 'paragraph':
        return (
          <Typography key={blockKey} variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
            {block.props?.text || ''}
          </Typography>
        );
      case 'divider':
        return <Divider key={blockKey} sx={{ my: 2 }} />;
      case 'image':
        return block.props?.url ? (
          <Box key={blockKey} sx={{ my: 2 }}>
            <Box component="img" src={block.props.url} alt={block.props?.alt || ''} sx={{ maxWidth: '100%', height: 'auto', borderRadius: 1 }} />
          </Box>
        ) : null;
      case 'row': {
        const children = Array.isArray(block.children) ? block.children : [];
        return (
          <Box key={blockKey} sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'stretch', my: 2 }}>
            {children.map((child, idx) => renderBlock(child, `${blockKey}-row-${idx}`))}
          </Box>
        );
      }
      case 'column': {
        const children = Array.isArray(block.children) ? block.children : [];
        return (
          <Box key={blockKey} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
            {children.map((child, idx) => renderBlock(child, `${blockKey}-col-${idx}`))}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 720, mx: 'auto', mb: 2 }}>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/supplier/agreements')}>
            Back
          </Button>
          {['draft', 'sent', 'scheduled'].includes(proposal?.status) && (
            <>
              <Button variant="outlined" startIcon={<CopySimple size={18} />} onClick={handleSaveAsTemplate} disabled={savingTemplate}>
                {savingTemplate ? <CircularProgress size={18} /> : 'Save as template'}
              </Button>
              <Button variant="outlined" startIcon={<Pencil size={18} />} onClick={() => navigate(`/supplier/agreements/${id}/edit`)}>
                Edit
              </Button>
            </>
          )}
          {['draft', 'scheduled'].includes(proposal?.status) && (
            <>
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={18} /> : <PaperPlaneTilt size={18} />}
                onClick={handleSend}
                disabled={sending}
                sx={{
                  bgcolor: '#f06292',
                  color: '#fff',
                  '&:hover': { bgcolor: '#ec407a' }
                }}
              >
                Send
              </Button>
            </>
          )}
          {proposal?.status === 'accepted' && isOriginal && (
            <Button
              variant="outlined"
              startIcon={<PlusCircle size={18} />}
              onClick={handleCreateAddendum}
              disabled={creatingAddendum}
            >
              {creatingAddendum ? <CircularProgress size={18} /> : 'Create addendum'}
            </Button>
          )}
          {proposal?.status === 'sent' && proposal?.publicToken && (
            <Button
              variant="outlined"
              startIcon={<Copy size={18} />}
              onClick={() => {
                navigator.clipboard?.writeText(`${clientUrl}/agreement/${proposal.publicToken}`);
              }}
            >
              Copy link
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {proposalTitle}
          </Typography>
          <Chip label={proposal?.status} size="small" color={STATUS_COLORS[proposal?.status]} sx={{ mt: 1.25 }} />
          <Divider sx={{ my: 2 }} />

          {layoutBlocks.length > 0 && (
            <>
              {layoutBlocks.map((block, idx) => renderBlock(block, `root-${idx}`))}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {legacyFreeform && proposal?.message && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {proposal.message}
              </Typography>
            </Box>
          )}

          {legacyFreeform && proposal?.message && <Divider sx={{ my: 2 }} />}

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Services & pricing
          </Typography>
          {renderPricingContent()}

          {proposal?.termsText && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Terms & conditions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {proposal.termsText}
              </Typography>
            </>
          )}

          {proposal?.sentAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Sent {format(new Date(proposal.sentAt), 'dd MMM yyyy HH:mm')}
            </Typography>
          )}
          {proposal?.updatedAfterSendAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Updated {format(new Date(proposal.updatedAfterSendAt), 'dd MMM yyyy HH:mm')}
            </Typography>
          )}
          {groupedThread.length > 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Agreement thread
              </Typography>
              {groupedThread.map((item) => (
                <Typography key={item._id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.agreementType === 'addendum' ? `Addendum v${item.versionNumber || '2'}` : 'Original agreement'} - {item.status}
                </Typography>
              ))}
            </Box>
          )}

          {isAccepted && proposal?.acceptance && (
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            >
              <AgreementAcceptedAdditionalInfo
                responses={proposal.acceptance.additionalInformationResponses}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
                Acceptance record
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  '& li': { mb: 0.5 }
                }}
              >
                <li>
                  <strong>Accepted at:</strong>{' '}
                  {format(new Date(proposal.acceptance.acceptedAt), "d MMMM yyyy 'at' HH:mm")} (UK)
                </li>
                <li>
                  <strong>Contract version:</strong> {proposal.acceptance.contractVersion || '1.0'}
                </li>
                {proposal.acceptance.signerName && (
                  <li>
                    <strong>Signed name:</strong> {proposal.acceptance.signerName}
                  </li>
                )}
                {proposal.acceptance.acceptedBy && (
                  <li>
                    <strong>Accepted by:</strong>{' '}
                    {[proposal.acceptance.acceptedBy.firstName, proposal.acceptance.acceptedBy.lastName]
                      .filter(Boolean)
                      .join(' ')}
                    {proposal.acceptance.acceptedBy.email && (
                      <> ({proposal.acceptance.acceptedBy.email})</>
                    )}
                  </li>
                )}
                {proposal.acceptance.ipAddress && (
                  <li>
                    <strong>IP address:</strong> {proposal.acceptance.ipAddress}
                  </li>
                )}
                {proposal.acceptance.userAgent && (
                  <li>
                    <strong>User agent:</strong>{' '}
                    <Box component="span" sx={{ wordBreak: 'break-all' }}>
                      {proposal.acceptance.userAgent}
                    </Box>
                  </li>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default ProposalDetail;
